import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../../../components/reusable/Seo/Seo.js';
import PageHero from '../../../components/ui/PageHero.js';
import Reveal from '../../../components/ui/Reveal.js';
import { faqPageSchema } from '../../../lib/schema.js';
import './WhatWeDo.scss';

/* ---- Data ---- */
const DIRECTION_LEDGER = [
  {
    n: '01',
    t: 'Written admission criteria',
    d: 'Who belongs on the unit is decided by criteria, not by instinct. Admission standards are documented, applied consistently, and defensible on review.',
  },
  {
    n: '02',
    t: 'Clinical guidelines by diagnosis group',
    d: 'Stroke, spinal cord injury, brain injury, and the other core rehabilitation diagnoses each carry written guidelines, so the standard of care doesn’t depend on who’s rounding.',
  },
  {
    n: '03',
    t: 'Liaison management',
    d: 'Clinical liaisons work from the same written criteria as the physicians, so referrals are screened consistently and the right patients arrive at the right time.',
  },
  {
    n: '04',
    t: 'Documentation standards',
    d: 'Notes are held to a written standard that establishes medical necessity, from admission through discharge.',
  },
  {
    n: '05',
    t: 'Quality metrics',
    d: 'Outcomes, functional gains, and program performance are measured and reviewed on a regular cadence, because a standard you don’t track isn’t a standard.',
  },
  {
    n: '06',
    t: 'Throughput aligned with quality',
    d: 'Length of stay and discharge planning are managed deliberately, so the unit moves without ever trading volume against outcomes.',
  },
];

const OS_CAPABILITIES = [
  {
    t: 'Charge capture without the clicks',
    d: 'A custom charge-capture and patient-management platform, built in-house and engineered to minimize clicks and friction, so the business of the unit keeps pace with the medicine.',
  },
  {
    t: 'Documentation that protects the work',
    d: 'An assistive PM&R documentation tool that speeds notes, preserves cognitive energy for complex patients, and builds in the language IRF reimbursement requires, so facilities field fewer documentation queries.',
  },
  {
    t: 'APP support that scales with census',
    d: 'Advanced practice providers hired, trained, and managed by the practice when volume justifies it. This extends physician reach without diluting the standard.',
  },
  {
    t: 'A full administrative backbone',
    d: 'Scheduling, credentialing, onboarding, and billing handled centrally, with growth mentorship from physicians who have built units before.',
  },
];

const LEADERSHIP_POINTS = [
  {
    t: 'Reliable staffing and retention',
    d: 'We staff units the way a high-performing private practice would, and our physicians have real reasons to stay.',
  },
  {
    t: 'Serious medical directorship',
    d: 'Written admission criteria, liaison management, documentation standards, and quality metrics: an operating role, not a name on a door.',
  },
  {
    t: 'Documentation that supports your reimbursement',
    d: 'Notes built for what reimbursement requires. Fewer queries land on your facility.',
  },
  {
    t: 'Throughput aligned with quality',
    d: 'Census and length of stay managed deliberately, for growth that holds up under outcome review.',
  },
  {
    t: 'Fast, direct responsiveness',
    d: 'A focused group, not a multi-layer corporate chain. Decisions get made by people you can call.',
  },
];

const FAQS = [
  {
    question: 'Who are the doctors caring for me on a VRP-served unit?',
    answer:
      'Board-certified rehabilitation physicians, specialists in physical medicine and rehabilitation (PM&R), direct the medical care on the units we serve. Your physician oversees your medical plan throughout your rehabilitation stay and coordinates the other specialists involved in your recovery.',
  },
  {
    question: 'What does a physiatrist do?',
    answer:
      'A physiatrist is a physician who specializes in restoring function after serious illness or injury, such as stroke, spinal cord injury, brain injury, or major surgery. On an inpatient rehabilitation unit, the physiatrist manages your medical conditions while guiding the therapy plan that helps you regain independence.',
  },
  {
    question: 'How does my care team work together?',
    answer:
      'Your rehabilitation physician works alongside nurses, physical, occupational, and speech therapists, and case managers as one team. The team meets regularly to review your progress and adjust your plan, with your goals at the center of every decision.',
  },
  {
    question: 'How do I reach the practice?',
    answer:
      'The simplest way is through the contact page on this site. Your message will be routed to the right person. For questions about day-to-day care on the unit, your bedside team can also reach your physician directly.',
  },
];

/* ---- 2. Core service: medical direction & unit management ---- */
function MedicalDirection() {
  return (
    <section className="u-section wwd-direction">
      <div className="u-container wwd-direction__grid">
        <div className="wwd-direction__intro">
          <Reveal>
            <span className="u-eyebrow">Core service</span>
            <h2>Medical direction &amp; unit management.</h2>
            <p className="wwd-direction__lead">
              On every unit we direct, the standards are written down, applied
              daily, and owned by the physicians who work there, so quality is
              repeatable, not personality-dependent.
            </p>
            <Link to="/contact" className="btn btn--ghost">
              Talk to us <span className="btn__arrow" aria-hidden="true">→</span>
            </Link>
          </Reveal>
        </div>
        <ol className="wwd-direction__list">
          {DIRECTION_LEDGER.map((item, i) => (
            <Reveal as="li" key={item.n} delay={0.04 * i} className="wwd-direction__item">
              <span className="wwd-direction__num">{item.n}</span>
              <div>
                <h3>{item.t}</h3>
                <p>{item.d}</p>
              </div>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ---- 3 & 4. Acute care consults + emerging service lines ---- */
function BeyondTheUnit() {
  return (
    <section className="u-section wwd-adjacent">
      <div className="u-container">
        <Reveal className="wwd-adjacent__head">
          <span className="u-eyebrow">Beyond the unit</span>
          <h2>Upstream of rehab, and ahead of it.</h2>
        </Reveal>
        <div className="wwd-adjacent__grid">
          <Reveal as="article" className="wwd-feature">
            <span className="wwd-card__tag">Acute care consults</span>
            <h3>Rehabilitation judgment, earlier in the hospital stay.</h3>
            <p>
              We run consult services in acute care settings, where the
              trajectory of a rehabilitation patient is often decided. Our
              physicians identify rehab candidates early, by the same written
              criteria used on our units. The result: fewer missed candidates,
              fewer misplaced admissions, and a smoother handoff in between.
            </p>
          </Reveal>
          <div className="wwd-adjacent__stack">
            <Reveal as="article" className="wwd-card" delay={0.08}>
              <span className="wwd-card__tag">Emerging service lines</span>
              <h3>Adjacent services, built to the same standard.</h3>
              <p>
                Where rehabilitation care extends beyond the inpatient unit, we
                build deliberately, adding service lines only when we can run
                them with the same written standards and physician ownership.
              </p>
            </Reveal>
            <Reveal as="article" className="wwd-card" delay={0.16}>
              <span className="wwd-card__tag">New markets</span>
              <h3>Growing deliberately.</h3>
              <p>
                We’ve operated multi-unit inpatient rehab coverage across
                Virginia since 2021, and we’re expanding into new markets,
                bringing the full operating system with us from day one.
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---- 5. The operating system (dark band) ---- */
function OperatingSystem() {
  return (
    <section className="wwd-os u-grain">
      <div className="u-container wwd-os__grid">
        <div className="wwd-os__copy">
          <Reveal>
            <span className="u-eyebrow u-eyebrow--signal">The operating system</span>
            <h2>
              The infrastructure our units <em>run on.</em>
            </h2>
            <p className="wwd-os__lead">
              We build our own software and write our own playbooks. Every
              system below exists because our physicians needed it on a real
              unit, and they use it every day.
            </p>
          </Reveal>
          <ul className="wwd-os__list">
            {OS_CAPABILITIES.map((c, i) => (
              <Reveal as="li" key={c.t} delay={0.05 * i}>
                <h3>{c.t}</h3>
                <p>{c.d}</p>
              </Reveal>
            ))}
          </ul>
        </div>
        <Reveal className="wwd-os__media" delay={0.12}>
          <div className="wwd-os__frame">
            <div className="wwd-os__app" aria-hidden="true">
              <div className="wwd-os__app-bar">
                <span className="wwd-os__app-logo">VRP</span>
                <span className="wwd-os__app-where">Rounding &middot; 4 West</span>
                <span className="wwd-os__app-day">Today</span>
              </div>
              <ul className="wwd-os__app-rows">
                <li className="wwd-os__app-row">
                  <span className="wwd-os__app-room">412</span>
                  <span className="wwd-os__app-dx">Stroke (CVA)</span>
                  <span className="wwd-os__app-pill is-done">Note ready</span>
                </li>
                <li className="wwd-os__app-row is-active">
                  <span className="wwd-os__app-room">418</span>
                  <span className="wwd-os__app-dx">Joint replacement</span>
                  <span className="wwd-os__app-pill is-review">Review charges</span>
                </li>
                <li className="wwd-os__app-row">
                  <span className="wwd-os__app-room">421</span>
                  <span className="wwd-os__app-dx">Brain injury</span>
                  <span className="wwd-os__app-pill is-set">Criteria met</span>
                </li>
              </ul>
              <div className="wwd-os__app-foot">
                <span><strong>Charge capture</strong> 3 of 3 captured</span>
                <span><strong>Documentation</strong> on track</span>
              </div>
            </div>
          </div>
          <p className="wwd-os__caption">
            Built in-house, refined on the unit: software shaped by the
            physicians who depend on it.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/* ---- 6. For hospital leadership ---- */
function HospitalLeadership() {
  return (
    <section className="u-section wwd-leaders">
      <div className="u-container">
        <Reveal className="wwd-leaders__head">
          <span className="u-eyebrow">For hospital leadership</span>
          <h2>How a hospital experiences VRP.</h2>
          <p className="wwd-leaders__lead">
            From the administrative side, a well-run rehab unit looks like
            stability: physicians who stay, documentation that holds up, and a
            program that grows without drama.
          </p>
        </Reveal>
        <div className="wwd-leaders__grid">
          <ul className="wwd-leaders__points">
            {LEADERSHIP_POINTS.map((p, i) => (
              <Reveal as="li" key={p.t} delay={0.04 * i}>
                <h3>{p.t}</h3>
                <p>{p.d}</p>
              </Reveal>
            ))}
          </ul>
          <Reveal as="aside" className="wwd-rank" delay={0.1}>
            <span className="wwd-rank__path" aria-hidden="true">
              <em>4th</em>
              <svg viewBox="0 0 120 40" preserveAspectRatio="none" aria-hidden="true">
                <path
                  d="M4 34 C 40 34, 70 8, 116 6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
              </svg>
              <strong>1st</strong>
            </span>
            <p className="wwd-rank__label">
              One of our units rose from 4th to 1st in its area for annual
              rehabilitation volume, with strong outcomes.
            </p>
            <Link to="/contact?audience=facility" className="btn btn--gold wwd-rank__cta">
              Talk to us about your unit{' '}
              <span className="btn__arrow" aria-hidden="true">→</span>
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---- 7. Patients & families FAQ ---- */
function PatientFaq() {
  return (
    <section className="u-section wwd-faq">
      <div className="u-container u-container--narrow">
        <Reveal className="wwd-faq__head">
          <span className="u-eyebrow">For patients &amp; families</span>
          <h2>Questions families ask.</h2>
        </Reveal>
        <div className="wwd-faq__list">
          {FAQS.map((f, i) => (
            <Reveal key={f.question} delay={0.04 * i}>
              <details className="wwd-faq__item">
                <summary>
                  <span className="wwd-faq__q">{f.question}</span>
                  <span className="wwd-faq__marker" aria-hidden="true" />
                </summary>
                <p>{f.answer}</p>
              </details>
            </Reveal>
          ))}
        </div>
        <Reveal className="wwd-faq__more">
          <Link to="/contact" className="wwd-faq__link">
            Contact the practice <span aria-hidden="true">→</span>
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function WhatWeDo() {
  return (
    <div className="wwd">
      <Seo
        title="What We Do"
        description="Virginia Rehab Physicians medically directs and manages inpatient rehabilitation units inside hospitals, runs acute care consult services, and builds the written standards and software that make quality rehabilitation care repeatable."
        path="/what-we-do"
        jsonLd={[faqPageSchema(FAQS)]}
      />

      <PageHero
        eyebrow="What we do"
        title={
          <>
            Inpatient rehabilitation, <em>run well.</em>
          </>
        }
        lede="We medically direct and manage inpatient rehabilitation units inside hospitals, run acute care consult services, and optimize rehabilitation care, all with written standards, purpose-built software, and accountable physician leadership."
        primary={{ label: 'Talk to us', href: '/contact' }}
        secondary={{ label: 'Join our team', href: '/careers' }}
      />

      <MedicalDirection />
      <BeyondTheUnit />
      <OperatingSystem />
      <HospitalLeadership />
      <PatientFaq />
    </div>
  );
}

export default WhatWeDo;
